import { useState } from 'react';

import {
  CalendarDays,
  Check,
  ChevronDown,
  Clock,
  ExternalLink,
  Pencil,
  Phone,
  Trash2,
  User,
  X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useDeleteBooking, useSetAttendance, useUpdateBooking } from '@/hooks/useBookings';
import { asBookingStatus, bookingStatusLabel, bookingStatusTone } from '@/lib/booking-status';

import { BOOKING_STATUS_LABELS, BOOKING_STATUSES } from '@repo/shared-types';

import type { BookingStatus, BookingWithRelations } from '@repo/shared-types';

interface BookingDrawerProps {
  booking: BookingWithRelations | null;
  onClose: () => void;
  onEdit: (booking: BookingWithRelations) => void;
}

export const BookingDrawer = ({ booking, onClose, onEdit }: BookingDrawerProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const deleteBooking = useDeleteBooking();
  const setAttendance = useSetAttendance();
  const updateBooking = useUpdateBooking();
  const [rawDataOpen, setRawDataOpen] = useState(false);

  if (!booking) return null;

  const bookingData = booking.data ?? null;
  const hasData = !!bookingData && Object.keys(bookingData).length > 0;
  const vehicle =
    bookingData &&
    typeof bookingData.vehicle === 'object' &&
    bookingData.vehicle !== null &&
    !Array.isArray(bookingData.vehicle)
      ? (bookingData.vehicle as Record<string, unknown>)
      : null;
  const asText = (v: unknown): string | null =>
    typeof v === 'string' && v.trim() !== '' ? v : typeof v === 'number' ? String(v) : null;
  const price =
    typeof vehicle?.price === 'number'
      ? `£${vehicle.price.toLocaleString()}`
      : asText(vehicle?.price);
  const dataRows = hasData
    ? [
        { label: 'Customer', value: asText(bookingData.customer_name) },
        { label: 'Phone', value: asText(bookingData.phone), mono: true },
        { label: 'Showroom', value: asText(bookingData.showroom) },
        { label: 'Vehicle', value: asText(vehicle?.name) },
        { label: 'Registration', value: asText(vehicle?.registration), mono: true },
        { label: 'Price', value: price },
      ].filter((row) => row.value)
    : [];
  const vdpUrl = asText(vehicle?.vdp_url);

  const statusTone = bookingStatusTone(booking.status);
  const isCancelled = booking.status === 'cancelled';
  const attendance = (booking as BookingWithRelations & { attendance?: string | null }).attendance;
  const isAttended = attendance === 'attended';
  const isNotAttended = attendance === 'not_attended';
  const hasTime = (booking as BookingWithRelations & { has_time?: boolean }).has_time !== false;

  const bookingDate = new Date(booking.date);
  const formattedDate = bookingDate.toLocaleDateString('en-GB', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  const formattedTime = bookingDate.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  const handleAttendance = async (value: 'attended' | 'not_attended' | null) => {
    try {
      await setAttendance.mutateAsync({ id: booking.id, attendance: value });
      const labels = { attended: 'Marked as attended', not_attended: 'Marked as not attended' };
      toast({ title: value ? labels[value] : 'Attendance cleared' });
    } catch {
      toast({ title: 'Failed to update attendance', variant: 'destructive' });
    }
  };

  const handleStatus = async (status: BookingStatus) => {
    if (status === booking.status) return;
    try {
      await updateBooking.mutateAsync({ id: booking.id, data: { status } });
      toast({ title: `Marked as ${BOOKING_STATUS_LABELS[status].toLowerCase()}` });
    } catch {
      toast({ title: 'Failed to update status', variant: 'destructive' });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteBooking.mutateAsync(booking.id);
      toast({ title: 'Booking deleted' });
      onClose();
    } catch {
      toast({ title: 'Failed to delete booking', variant: 'destructive' });
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/20 backdrop-blur-[2px]"
        onClick={onClose}
        role="presentation"
      />
      <div className="bg-background animate-in slide-in-from-right fixed inset-y-0 right-0 z-50 flex w-[500px] flex-col shadow-2xl duration-200">
        <div className="flex items-center justify-between px-6 py-4">
          <h2 className="text-base font-semibold">Booking Details</h2>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-6 pb-6">
          <div>
            <div className="mb-3 flex items-center gap-2">
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${statusTone.badge}`}
              >
                <span className={`mr-1.5 h-1.5 w-1.5 rounded-full ${statusTone.dot}`} />
                {bookingStatusLabel(booking.status)}
              </span>
              {isAttended && (
                <span className="inline-flex items-center rounded-full bg-green-500/10 px-2 py-0.5 text-[11px] font-medium text-green-500">
                  <Check className="mr-1 h-3 w-3" />
                  Attended
                </span>
              )}
              {isNotAttended && (
                <span className="inline-flex items-center rounded-full bg-red-500/10 px-2 py-0.5 text-[11px] font-medium text-red-500">
                  <X className="mr-1 h-3 w-3" />
                  Not Attended
                </span>
              )}
            </div>
            <h3
              className={`text-lg leading-tight font-semibold ${statusTone.struck ? 'line-through opacity-60' : ''}`}
            >
              {booking.title}
            </h3>
          </div>

          <div className="bg-muted/30 flex items-center gap-4 rounded-lg p-3.5">
            <div className="flex items-center gap-2 text-sm">
              <CalendarDays className="text-muted-foreground h-4 w-4" />
              <span>{formattedDate}</span>
            </div>
            {hasTime && (
              <>
                <div className="bg-border h-4 w-px" />
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="text-muted-foreground h-4 w-4" />
                  <span>{formattedTime}</span>
                </div>
              </>
            )}
          </div>

          {booking.description && (
            <div>
              <p className="text-muted-foreground mb-1.5 text-xs font-medium tracking-wider uppercase">
                Description
              </p>
              <p className="text-sm leading-relaxed whitespace-pre-line">{booking.description}</p>
            </div>
          )}

          {hasData && (
            <div className="border-border/40 rounded-lg border p-4">
              <p className="text-muted-foreground mb-3 text-xs font-medium tracking-wider uppercase">
                Booking Data
              </p>
              {(dataRows.length > 0 || vdpUrl) && (
                <div className="mb-3 space-y-2">
                  {dataRows.map((row) => (
                    <div
                      key={row.label}
                      className="flex items-baseline justify-between gap-4 text-sm"
                    >
                      <span className="text-muted-foreground text-xs">{row.label}</span>
                      <span className={`text-right ${row.mono ? 'font-mono text-xs' : ''}`}>
                        {row.value}
                      </span>
                    </div>
                  ))}
                  {vdpUrl && (
                    <div className="flex items-baseline justify-between gap-4 text-sm">
                      <span className="text-muted-foreground text-xs">Listing</span>
                      <a
                        href={vdpUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary inline-flex items-center gap-1 text-xs font-medium hover:underline"
                      >
                        View vehicle
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  )}
                </div>
              )}
              <Collapsible open={rawDataOpen} onOpenChange={setRawDataOpen}>
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground flex items-center gap-1.5 text-xs font-medium"
                  >
                    Raw JSON
                    <ChevronDown
                      className={`h-3 w-3 transition-transform ${rawDataOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <pre className="bg-muted/30 mt-2 max-h-48 overflow-auto rounded-md p-2 font-mono text-[11px] whitespace-pre-wrap">
                    {JSON.stringify(bookingData, null, 2)}
                  </pre>
                </CollapsibleContent>
              </Collapsible>
            </div>
          )}

          {booking.call && (
            <div className="border-border/40 rounded-lg border p-4">
              <p className="text-muted-foreground mb-3 text-xs font-medium tracking-wider uppercase">
                Linked Contact
              </p>
              <div className="space-y-2.5">
                {booking.call.contact?.name && (
                  <div className="flex items-center gap-2.5 text-sm">
                    <User className="text-muted-foreground h-3.5 w-3.5" />
                    <span>{booking.call.contact.name}</span>
                  </div>
                )}
                {booking.call.contact?.phone && (
                  <div className="flex items-center gap-2.5 text-sm">
                    <Phone className="text-muted-foreground h-3.5 w-3.5" />
                    <span className="font-mono text-xs">{booking.call.contact.phone}</span>
                  </div>
                )}
                {booking.call.campaign?.name && (
                  <div className="flex items-center gap-2.5 text-sm">
                    <span className="text-muted-foreground ml-0.5 h-3 w-3 text-[10px]">#</span>
                    <span className="text-primary text-xs font-medium">
                      {booking.call.campaign.name}
                    </span>
                  </div>
                )}
                {!booking.call.contact?.name &&
                  !booking.call.contact?.phone &&
                  !booking.call.campaign?.name && (
                    <p className="text-muted-foreground text-xs">
                      No contact details on linked call
                    </p>
                  )}
              </div>
            </div>
          )}

          <div className="text-muted-foreground pt-2 text-xs">
            Created {new Date(booking.created_at as string).toLocaleString()}
          </div>
        </div>

        <div className="border-border/40 space-y-3 border-t px-6 py-4">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground shrink-0 text-xs">Status</span>
            <Select
              value={asBookingStatus(booking.status) ?? undefined}
              onValueChange={(v) => void handleStatus(v as BookingStatus)}
              disabled={updateBooking.isPending}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder={bookingStatusLabel(booking.status)} />
              </SelectTrigger>
              <SelectContent>
                {BOOKING_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {BOOKING_STATUS_LABELS[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {!isCancelled && (
            <div className="flex gap-2">
              <Button
                size="sm"
                variant={isAttended ? 'default' : 'outline'}
                className="flex-1 gap-1.5"
                onClick={() => handleAttendance(isAttended ? null : 'attended')}
                disabled={setAttendance.isPending}
              >
                <Check className="h-3.5 w-3.5" />
                {isAttended ? 'Undo' : 'Attended'}
              </Button>
              <Button
                size="sm"
                variant={isNotAttended ? 'destructive' : 'outline'}
                className="flex-1 gap-1.5"
                onClick={() => handleAttendance(isNotAttended ? null : 'not_attended')}
                disabled={setAttendance.isPending}
              >
                <X className="h-3.5 w-3.5" />
                {isNotAttended ? 'Undo' : 'Not Attended'}
              </Button>
            </div>
          )}
          <div className="flex items-center gap-2">
            {booking.call && (
              <Button
                variant="outline"
                size="sm"
                className="flex-1 gap-1.5"
                onClick={() => navigate(`/calls/${booking.call!.id}`)}
              >
                <ExternalLink className="h-3.5 w-3.5" />
                View Call
              </Button>
            )}
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => onEdit(booking)}>
              <Pencil className="h-3.5 w-3.5" />
              Edit
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive gap-1.5"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this booking?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently remove &ldquo;{booking.title}&rdquo;. This action cannot
                    be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>
    </>
  );
};
